/**
 * Compliance orchestration types
 *
 * Types for unified compliance verification flows that combine
 * geolocation verification with customer risk profiling.
 */

import type { LocationVerification, DeviceFingerprintRequest } from '../geolocation/types';
import type { ValidateCipherTextResponse } from '../geolocation/types';
import type { CustomerProfile, EntityType } from '../risk-profile/types';

// ============================================================================
// Registration Verification Types
// ============================================================================

export interface RegistrationVerificationRequest {
  // Identity
  customerId: string;
  fullName: string;
  emailAddress: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  address?: string;
  entityType?: EntityType;

  // Geolocation
  ipAddress: string;
  deviceFingerprint?: DeviceFingerprintRequest;
  cipherText?: string;

  // Additional data
  metadata?: Record<string, unknown>;
}

export interface RegistrationVerificationResponse {
  allowed: boolean;
  geolocation: LocationVerification;
  profile: CustomerProfile | null;
  requiresKYC: boolean;
  requiresEDD: boolean;
  blockReasons: string[];
  processingTime: number;
  cipherTextValidation?: ValidateCipherTextResponse;
}

// ============================================================================
// Login Verification Types
// ============================================================================

export interface LoginVerificationRequest {
  customerId: string;
  ipAddress: string;
  deviceFingerprint?: DeviceFingerprintRequest;
  cipherText?: string;
  metadata?: Record<string, unknown>;
}

export interface LoginVerificationResponse {
  allowed: boolean;
  geolocation: LocationVerification;
  profile: CustomerProfile | null;
  requiresStepUp: boolean;
  blockReasons: string[];
  processingTime: number;
  cipherTextValidation?: ValidateCipherTextResponse;
}

// ============================================================================
// Transaction Verification Types
// ============================================================================

export interface TransactionVerificationRequest {
  customerId: string;
  ipAddress: string;
  amount: number;
  currency: string;
  transactionType?: 'deposit' | 'withdrawal' | 'bet' | 'transfer' | 'payout';
  deviceFingerprint?: DeviceFingerprintRequest;
  cipherText?: string;
  metadata?: Record<string, unknown>;
}

export interface TransactionVerificationResponse {
  allowed: boolean;
  geolocation: LocationVerification;
  profile: CustomerProfile | null;
  transactionRisk: TransactionRiskResult;
  requiresApproval: boolean;
  blockReasons: string[];
  processingTime: number;
  cipherTextValidation?: ValidateCipherTextResponse;
}

export interface TransactionRiskResult {
  score: number;
  level: 'low' | 'medium' | 'high' | 'critical';
  factors: string[];
  allowed: boolean;
  requiresManualReview: boolean;
}

// ============================================================================
// Generic Event Verification Types
// ============================================================================

export interface EventVerificationRequest {
  customerId: string;
  ipAddress: string;
  eventType: string;
  deviceFingerprint?: DeviceFingerprintRequest;
  cipherText?: string;
  metadata?: Record<string, unknown>;
}

export interface EventVerificationResponse {
  allowed: boolean;
  geolocation: LocationVerification;
  blockReasons: string[];
  processingTime: number;
  cipherTextValidation?: ValidateCipherTextResponse;
}

// ============================================================================
// Currency Exchange Rates (for transaction risk calculation)
// ============================================================================

export interface CurrencyRates {
  [currency: string]: number; // Rate to USD
}

export const DEFAULT_CURRENCY_RATES: CurrencyRates = {
  USD: 1.0,
  EUR: 1.1,
  GBP: 1.27,
  CAD: 0.74,
  AUD: 0.66,
  JPY: 0.0067,
  CHF: 1.13,
  CNY: 0.14,
  INR: 0.012,
  BRL: 0.20,
  MXN: 0.058,
  ZAR: 0.055,
};

// ============================================================================
// Location Request Types (re-exported from geolocation for convenience)
// ============================================================================

export type {
  LocationRequest,
  LocationRequestStatus,
  LocationRequestChannel,
  CreateLocationRequestRequest,
  LocationRequestResult,
  LocationRequestFilters,
  LocationRequestListResponse,
  ResendLocationRequestRequest,
  LocationShareInfo,
  LocationCaptureRequest,
  LocationCaptureResponse,
} from '../geolocation/types';

// ============================================================================
// Compliance-specific Location Request Types
// ============================================================================

export interface ComplianceLocationRequestInput {
  /** Customer user ID to request location from */
  customerId: string;
  /** Notification channel to use */
  channel: 'sms' | 'email' | 'push';
  /** Reason for requesting location (for compliance/audit) */
  reason: string;
  /** Customer email (required for email channel) */
  email?: string;
  /** Customer phone (required for SMS channel) */
  phone?: string;
  /** Custom expiry time in hours (default: 24) */
  expiryHours?: number;
}

export interface ComplianceLocationRequestResult {
  /** The created location request */
  request: import('../geolocation/types').LocationRequest;
  /** Shareable link for the customer */
  shareLink: string;
  /** Token expiration time */
  tokenExpiry: string;
  /** Customer profile (if available) */
  profile?: import('../risk-profile/types').CustomerProfile;
}
